import * as THREE from '../vendor/three.module.js';
import { CSG } from '../vendor/three-csg-ts/index.js';
const MM_TO_UNITS = 0.01;
const aluminumTextureCache = new Map();

const hash2 = (x, y, seed) => {
    const n = Math.sin((x * 127.1 + y * 311.7 + seed * 91.97) * 0.017453292519943295) * 43758.5453123;
    return n - Math.floor(n);
};

const makeRawAluminumMaps = (baseHex) => {
    const cacheKey = String(baseHex).toLowerCase();
    const cached = aluminumTextureCache.get(cacheKey);
    if (cached) {
        return cached;
    }
    const size = 512;
    const colorData = new Uint8Array(size * size * 4);
    const roughData = new Uint8Array(size * size * 4);
    const metalData = new Uint8Array(size * size * 4);
    const base = new THREE.Color(baseHex);
    for (let y = 0; y < size; y += 1) {
        for (let x = 0; x < size; x += 1) {
            const i = (y * size + x) * 4;
            const coarseA = hash2(Math.floor(x / 64), Math.floor(y / 62), 11);
            const coarseB = hash2(Math.floor((x + 97) / 79), Math.floor((y + 53) / 73), 19);
            const medium = hash2(Math.floor(x / 24), Math.floor(y / 27), 23);
            const streak = hash2(Math.floor(x / 6), Math.floor(y / 120), 51);
            const coarse = coarseA * 0.62 + coarseB * 0.38;
            const tone = THREE.MathUtils.clamp(0.95 + (coarse - 0.5) * 0.11 + (medium - 0.5) * 0.025, 0.85, 1.03);
            const rough = THREE.MathUtils.clamp(0.75 + (streak - 0.5) * 0.2 + (medium - 0.5) * 0.08, 0.6, 0.93);
            const metal = THREE.MathUtils.clamp(0.9 + (coarse - 0.5) * 0.03 + (medium - 0.5) * 0.012, 0.84, 0.95);
            colorData[i] = Math.round(THREE.MathUtils.clamp(base.r * tone, 0, 1) * 255);
            colorData[i + 1] = Math.round(THREE.MathUtils.clamp(base.g * tone, 0, 1) * 255);
            colorData[i + 2] = Math.round(THREE.MathUtils.clamp(base.b * tone, 0, 1) * 255);
            colorData[i + 3] = 255;
            const roughByte = Math.round(rough * 255);
            roughData[i] = roughByte;
            roughData[i + 1] = roughByte;
            roughData[i + 2] = roughByte;
            roughData[i + 3] = 255;
            const metalByte = Math.round(metal * 255);
            metalData[i] = metalByte;
            metalData[i + 1] = metalByte;
            metalData[i + 2] = metalByte;
            metalData[i + 3] = 255;
        }
    }
    const colorMap = new THREE.DataTexture(colorData, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
    colorMap.colorSpace = THREE.SRGBColorSpace;
    colorMap.wrapS = THREE.RepeatWrapping;
    colorMap.wrapT = THREE.RepeatWrapping;
    colorMap.repeat.set(1, 1);
    colorMap.needsUpdate = true;
    const roughnessMap = new THREE.DataTexture(roughData, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
    roughnessMap.wrapS = THREE.RepeatWrapping;
    roughnessMap.wrapT = THREE.RepeatWrapping;
    roughnessMap.repeat.copy(colorMap.repeat);
    roughnessMap.needsUpdate = true;
    const metalnessMap = new THREE.DataTexture(metalData, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
    metalnessMap.wrapS = THREE.RepeatWrapping;
    metalnessMap.wrapT = THREE.RepeatWrapping;
    metalnessMap.repeat.copy(colorMap.repeat);
    metalnessMap.needsUpdate = true;
    const maps = { colorMap, roughnessMap, metalnessMap };
    aluminumTextureCache.set(cacheKey, maps);
    return maps;
};

const reprojectBodyUVs = (mesh, config) => {
    const geometry = mesh?.geometry;
    if (!geometry?.attributes?.position || !geometry?.attributes?.normal) {
        return;
    }
    const position = geometry.attributes.position;
    const normal = geometry.attributes.normal;
    const uv = new Float32Array(position.count * 2);
    const sx = Math.max(config.enclosure.width * MM_TO_UNITS, 0.001);
    const sy = Math.max(config.enclosure.depth * MM_TO_UNITS, 0.001);
    const sz = Math.max(config.enclosure.height * MM_TO_UNITS, 0.001);
    for (let i = 0; i < position.count; i += 1) {
        const x = position.getX(i);
        const y = position.getY(i);
        const z = position.getZ(i);
        const nx = Math.abs(normal.getX(i));
        const ny = Math.abs(normal.getY(i));
        const nz = Math.abs(normal.getZ(i));
        let u = 0;
        let v = 0;
        if (nz >= nx && nz >= ny) {
            u = x / sx + 0.5;
            v = y / sy + 0.5;
        }
        else if (ny >= nx) {
            u = x / sx + 0.5;
            v = z / sz + 0.5;
        }
        else {
            u = y / sy + 0.5;
            v = z / sz + 0.5;
        }
        uv[i * 2] = u;
        uv[i * 2 + 1] = v;
    }
    geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
};
const findHole = (config, holeId) => {
    const hole = config.holes.find((entry) => entry.id === holeId);
    if (!hole) {
        throw new Error(`Missing hole with id ${holeId}`);
    }
    return hole;
};
const roundedRectShape = (width, depth, radius) => {
    const hw = width / 2;
    const hd = depth / 2;
    const r = Math.min(radius, hw, hd);
    const shape = new THREE.Shape();
    shape.moveTo(-hw + r, -hd);
    shape.lineTo(hw - r, -hd);
    shape.absarc(hw - r, -hd + r, r, -Math.PI / 2, 0, false);
    shape.lineTo(hw, hd - r);
    shape.absarc(hw - r, hd - r, r, 0, Math.PI / 2, false);
    shape.lineTo(-hw + r, hd);
    shape.absarc(-hw + r, hd - r, r, Math.PI / 2, Math.PI, false);
    shape.lineTo(-hw, -hd + r);
    shape.absarc(-hw + r, -hd + r, r, Math.PI, (Math.PI * 3) / 2, false);
    return shape;
};
const addFrontHoleRecess = (group, id, xMm, yMm, diameterMm, topZ) => {
    const recessDepth = 2.2;
    const geometry = new THREE.CylinderGeometry((diameterMm / 2) * MM_TO_UNITS, (diameterMm / 2) * MM_TO_UNITS, recessDepth * MM_TO_UNITS, 32);
    const material = new THREE.MeshStandardMaterial({
        color: '#2b2e32',
        metalness: 0.45,
        roughness: 0.62
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = `HoleRecess-${id}`;
    mesh.rotation.x = Math.PI / 2;
    mesh.position.set(xMm * MM_TO_UNITS, yMm * MM_TO_UNITS, topZ - (recessDepth * MM_TO_UNITS) / 2);
    group.add(mesh);
};
const axisRotation = (axis) => axis === 'front' ? new THREE.Euler(Math.PI / 2, 0, 0) : new THREE.Euler(0, 0, 0);
const axisOffset = (axis, distanceUnits) => axis === 'front'
    ? new THREE.Vector3(0, 0, distanceUnits)
    : new THREE.Vector3(0, distanceUnits, 0);
const threadCoreGeometryCache = new Map();
const threadRidgeGeometryCache = new Map();
const hexNutGeometryCache = new Map();
const getThreadCoreGeometry = (radius, length, segments, openEnded) => {
    const key = `${radius.toFixed(6)}|${length.toFixed(6)}|${segments}|${openEnded ? 1 : 0}`;
    const cached = threadCoreGeometryCache.get(key);
    if (cached) {
        return cached;
    }
    const geometry = new THREE.CylinderGeometry(radius, radius, length, segments, 1, openEnded);
    threadCoreGeometryCache.set(key, geometry);
    return geometry;
};
const getThreadRidgeGeometry = (radius, tube) => {
    const key = `${radius.toFixed(6)}|${tube.toFixed(6)}`;
    const cached = threadRidgeGeometryCache.get(key);
    if (cached) {
        return cached;
    }
    const geometry = new THREE.TorusGeometry(radius, tube, 8, 16);
    threadRidgeGeometryCache.set(key, geometry);
    return geometry;
};
const makeHexShape = (radius) => {
    const shape = new THREE.Shape();
    for (let i = 0; i < 6; i += 1) {
        const a = (Math.PI / 3) * i + Math.PI / 6;
        const x = Math.cos(a) * radius;
        const y = Math.sin(a) * radius;
        if (i === 0) {
            shape.moveTo(x, y);
        }
        else {
            shape.lineTo(x, y);
        }
    }
    shape.closePath();
    return shape;
};
const getHexNutGeometry = (outerRadius, innerRadius, thickness) => {
    const key = `${outerRadius.toFixed(6)}|${innerRadius.toFixed(6)}|${thickness.toFixed(6)}`;
    const cached = hexNutGeometryCache.get(key);
    if (cached) {
        return cached;
    }
    const shape = makeHexShape(outerRadius);
    const hole = new THREE.Path();
    hole.absellipse(0, 0, innerRadius, innerRadius, 0, Math.PI * 2, false, 0);
    shape.holes.push(hole);
    const geometry = new THREE.ExtrudeGeometry(shape, {
        depth: thickness,
        bevelEnabled: false,
        steps: 1,
        curveSegments: 16
    });
    geometry.translate(0, 0, -thickness / 2);
    hexNutGeometryCache.set(key, geometry);
    return geometry;
};
const DEFAULT_THREADING = {
    ridgeCount: 10,
    ridgeDepth: 0.2,
    ridgeThickness: 0.12,
    contrast: 0.55,
    nutContrast: 0.22,
    threadLengthScale: 1.15,
    nutThicknessScale: 1,
    threadPeek: 0.7
};
const getThreadProfile = (config) => {
    const source = config.threading ?? DEFAULT_THREADING;
    return {
        ridgeCount: Math.max(2, Math.round(source.ridgeCount ?? DEFAULT_THREADING.ridgeCount)),
        ridgeDepth: Math.max(0.02, source.ridgeDepth ?? DEFAULT_THREADING.ridgeDepth),
        ridgeThickness: Math.max(0.02, source.ridgeThickness ?? DEFAULT_THREADING.ridgeThickness),
        contrast: THREE.MathUtils.clamp(source.contrast ?? DEFAULT_THREADING.contrast, 0, 1),
        nutContrast: THREE.MathUtils.clamp(source.nutContrast ?? DEFAULT_THREADING.nutContrast, 0, 1),
        threadLengthScale: Math.max(0.2, source.threadLengthScale ?? DEFAULT_THREADING.threadLengthScale),
        nutThicknessScale: Math.max(0.2, source.nutThicknessScale ?? DEFAULT_THREADING.nutThicknessScale),
        threadPeek: Math.max(0, source.threadPeek ?? DEFAULT_THREADING.threadPeek)
    };
};
const addThreadedMount = (group, options) => {
    const threadColor = new THREE.Color('#d6d9dd').lerp(new THREE.Color('#656b73'), options.profile.contrast);
    const nutColor = new THREE.Color('#c9ced3').lerp(new THREE.Color('#7f868f'), options.profile.nutContrast);
    const threadMaterial = new THREE.MeshStandardMaterial({
        color: threadColor,
        metalness: 0.96,
        roughness: 0.2
    });
    const nutMaterial = new THREE.MeshStandardMaterial({
        color: nutColor,
        metalness: 0.95,
        roughness: 0.28
    });
    const threadRadius = options.threadRadiusMm * MM_TO_UNITS;
    const nutThicknessMm = options.baseNutThicknessMm * options.profile.nutThicknessScale;
    const threadLengthMm = Math.max(options.baseThreadLengthMm * options.profile.threadLengthScale, nutThicknessMm + options.profile.threadPeek);
    const threadLength = threadLengthMm * MM_TO_UNITS;
    const nutRadius = options.nutRadiusMm * MM_TO_UNITS;
    const nutThickness = nutThicknessMm * MM_TO_UNITS;
    const coreOuterRadius = threadRadius * 0.9;
    const boreRadius = options.boreRadiusMm ? options.boreRadiusMm * MM_TO_UNITS : 0;
    const openEnded = boreRadius > 0;
    const coreRadius = openEnded ? Math.max(coreOuterRadius, boreRadius * 1.06) : coreOuterRadius;
    const coreGeometry = getThreadCoreGeometry(coreRadius, threadLength, 28, openEnded);
    const threadCore = new THREE.Mesh(coreGeometry, threadMaterial);
    threadCore.rotation.copy(axisRotation(options.axis));
    threadCore.position.copy(options.facePoint).add(axisOffset(options.axis, threadLength / 2));
    threadCore.name = `${options.id}-ThreadCore`;
    group.add(threadCore);
    const ridgeCount = options.profile.ridgeCount;
    const ridgeRadius = threadRadius * (1 + options.profile.ridgeDepth);
    const ridgeTube = Math.max(threadRadius * options.profile.ridgeThickness, 0.00045);
    for (let i = 0; i < ridgeCount; i += 1) {
        const t = i / (ridgeCount - 1);
        const offset = (t - 0.5) * threadLength;
        const ridge = new THREE.Mesh(getThreadRidgeGeometry(ridgeRadius, ridgeTube), threadMaterial);
        if (options.axis === 'side') {
            ridge.rotation.x = Math.PI / 2;
        }
        ridge.position
            .copy(options.facePoint)
            .add(axisOffset(options.axis, threadLength / 2 + offset));
        ridge.name = `${options.id}-ThreadRidge-${i + 1}`;
        group.add(ridge);
    }
    const minVisibleNutRadius = ridgeRadius * 1.3;
    const effectiveNutRadius = Math.max(nutRadius, minVisibleNutRadius);
    const nutInnerRadius = Math.max(threadRadius * 1.01, 0.0005);
    const nutGeometry = getHexNutGeometry(effectiveNutRadius, nutInnerRadius, nutThickness);
    const nutRing = new THREE.Mesh(nutGeometry, nutMaterial);
    if (options.axis === 'front') {
        nutRing.rotation.set(0, 0, 0);
    }
    else {
        nutRing.rotation.set(Math.PI / 2, 0, 0);
    }
    nutRing.position.copy(options.facePoint).add(axisOffset(options.axis, nutThickness / 2));
    nutRing.name = `${options.id}-HexNut`;
    group.add(nutRing);
};
const addBackScrews = (group, config) => {
    const x = (config.enclosure.width / 2 - config.backScrews.inset) * MM_TO_UNITS;
    const y = (config.enclosure.depth / 2 - config.backScrews.inset) * MM_TO_UNITS;
    const z = -(config.backScrews.headHeight * MM_TO_UNITS) / 2 - 0.001;
    const points = [
        [-x, -y],
        [x, -y],
        [-x, y],
        [x, y]
    ];
    points.forEach(([px, py], index) => {
        const screwGroup = new THREE.Group();
        screwGroup.name = `BackScrew-${index + 1}`;
        const head = new THREE.Mesh(new THREE.CylinderGeometry(config.backScrews.headRadius * MM_TO_UNITS, config.backScrews.headRadius * MM_TO_UNITS, config.backScrews.headHeight * MM_TO_UNITS, 28), new THREE.MeshStandardMaterial({
            color: config.backScrews.color,
            metalness: 0.95,
            roughness: 0.28
        }));
        head.rotation.x = Math.PI / 2;
        screwGroup.add(head);
        const slotMaterial = new THREE.MeshStandardMaterial({
            color: '#3c3f44',
            metalness: 0.2,
            roughness: 0.7
        });
        const slotA = new THREE.Mesh(new THREE.BoxGeometry(config.backScrews.slotLength * MM_TO_UNITS, config.backScrews.slotWidth * MM_TO_UNITS, config.backScrews.slotDepth * MM_TO_UNITS), slotMaterial);
        slotA.position.z = (config.backScrews.headHeight * MM_TO_UNITS) / 2;
        const slotB = slotA.clone();
        slotB.rotation.z = Math.PI / 2;
        screwGroup.add(slotA, slotB);
        screwGroup.position.set(px, py, z);
        group.add(screwGroup);
    });
};
const stateToTilt = (position) => {
    if (position === 'top') {
        return -0.38;
    }
    if (position === 'bottom') {
        return 0.38;
    }
    return 0;
};
const equallySpacedX = (count, spacing) => {
    if (count <= 0) {
        return [];
    }
    if (count === 1) {
        return [0];
    }
    const start = -((count - 1) * spacing) / 2;
    return Array.from({ length: count }, (_, i) => start + i * spacing);
};
const createKnobGeometry = (radiusMm, heightMm, topRoundnessMm) => {
    const r = radiusMm * MM_TO_UNITS;
    const h = heightMm * MM_TO_UNITS;
    const round = Math.min(topRoundnessMm * MM_TO_UNITS, h * 0.45, r * 0.55);
    const cylHeight = Math.max(h - round, h * 0.55);
    const points = [];
    points.push(new THREE.Vector2(0.0012, 0));
    points.push(new THREE.Vector2(r, 0));
    points.push(new THREE.Vector2(r, cylHeight));
    const arcSegments = 8;
    for (let i = 1; i <= arcSegments; i += 1) {
        const t = i / arcSegments;
        const theta = (Math.PI / 2) * t;
        const x = r - round + Math.cos(theta) * round;
        const y = cylHeight + Math.sin(theta) * round;
        points.push(new THREE.Vector2(Math.max(x, 0.0008), y));
    }
    points.push(new THREE.Vector2(0.0012, h));
    return new THREE.LatheGeometry(points, 44);
};
const carveTopSideJackOpenings = (bodyMesh, config, topZ) => {
    const faceProtrusionMm = 2.2;
    const sideY = (config.enclosure.depth / 2) * MM_TO_UNITS;
    const q = config.topSideJacks.quarterInch;
    const p = config.topSideJacks.power9v;
    const spanMm = Math.max(0, config.enclosure.width - q.edgeInset * 2);
    const quarterX = equallySpacedX(q.count, q.count > 1 ? spanMm / (q.count - 1) : 0);
    const quarterZ = topZ - q.zOffsetFromTop * MM_TO_UNITS;
    const powerZ = topZ - p.zOffsetFromTop * MM_TO_UNITS;
    const material = bodyMesh.material;
    const originalVertexCount = bodyMesh.geometry.attributes.position.count;
    let carved = bodyMesh;
    quarterX.forEach((xMm) => {
        const openingDepthMm = Math.max(q.recessDepth + 6, config.enclosure.depth * 0.62);
        const openingCenterY = sideY - (openingDepthMm * MM_TO_UNITS) / 2 + faceProtrusionMm * MM_TO_UNITS;
        const cutter = new THREE.Mesh(new THREE.CylinderGeometry((q.diameter / 2) * MM_TO_UNITS * 1.03, (q.diameter / 2) * MM_TO_UNITS * 1.03, openingDepthMm * MM_TO_UNITS, 32));
        cutter.position.set(xMm * MM_TO_UNITS, openingCenterY, quarterZ);
        cutter.updateMatrix();
        carved.updateMatrix();
        carved = CSG.subtract(carved, cutter);
    });
    const powerDepthMm = Math.max(p.recessDepth + 6.5, 11);
    const powerCutter = new THREE.Mesh(new THREE.CylinderGeometry((p.diameter / 2) * MM_TO_UNITS * 1.03, (p.diameter / 2) * MM_TO_UNITS * 1.03, Math.max(powerDepthMm, config.enclosure.depth * 0.62) * MM_TO_UNITS, 32));
    const powerCutDepthMm = Math.max(powerDepthMm, config.enclosure.depth * 0.62);
    powerCutter.position.set(0, sideY - (powerCutDepthMm * MM_TO_UNITS) / 2 + faceProtrusionMm * MM_TO_UNITS, powerZ);
    powerCutter.updateMatrix();
    carved.updateMatrix();
    carved = CSG.subtract(carved, powerCutter);
    carved.material = material;
    carved.geometry.computeVertexNormals();
    carved.name = 'Body';
    const carvedVertexCount = carved.geometry.attributes.position.count;
    if (carvedVertexCount === originalVertexCount) {
        console.warn('[pedal] jack cutout CSG did not change body geometry; check cutter placement');
    }
    return carved;
};
const addTopSideJacks = (group, config, topZ) => {
    const threadProfile = getThreadProfile(config);
    const showCutters = config.debug?.showJackCutters ?? false;
    const faceProtrusionMm = 2.2;
    const hd = (config.enclosure.depth / 2) * MM_TO_UNITS;
    const sideY = hd;
    const q = config.topSideJacks.quarterInch;
    const spanMm = Math.max(0, config.enclosure.width - q.edgeInset * 2);
    const quarterX = equallySpacedX(q.count, q.count > 1 ? spanMm / (q.count - 1) : 0);
    const quarterZ = topZ - q.zOffsetFromTop * MM_TO_UNITS;
    quarterX.forEach((xMm, index) => {
        const jackGroup = new THREE.Group();
        jackGroup.name = `TopQuarterJack-${index + 1}`;
        const openingDepthMm = Math.max(q.recessDepth + 6, config.enclosure.depth * 0.62);
        const openingCenterY = sideY - (openingDepthMm * MM_TO_UNITS) / 2 + faceProtrusionMm * MM_TO_UNITS;
        const ring = new THREE.Mesh(new THREE.TorusGeometry(((q.ringDiameter + q.diameter) / 4) * MM_TO_UNITS, Math.max(((q.ringDiameter - q.diameter) / 4) * MM_TO_UNITS, 0.0007), 12, 32), new THREE.MeshStandardMaterial({ color: '#d7dade', metalness: 0.95, roughness: 0.24 }));
        ring.rotation.x = Math.PI / 2;
        ring.position.y = sideY + 0.0005;
        jackGroup.add(ring);
        if (showCutters) {
            const helper = new THREE.Mesh(new THREE.CylinderGeometry((q.diameter / 2) * MM_TO_UNITS * 1.2, (q.diameter / 2) * MM_TO_UNITS * 1.2, openingDepthMm * MM_TO_UNITS, 20), new THREE.MeshBasicMaterial({
                color: '#ff2f92',
                transparent: true,
                opacity: 0.55,
                depthTest: false,
                side: THREE.DoubleSide
            }));
            helper.position.set(xMm * MM_TO_UNITS, openingCenterY, quarterZ);
            helper.renderOrder = 1000;
            group.add(helper);
        }
        jackGroup.position.set(xMm * MM_TO_UNITS, 0, quarterZ);
        group.add(jackGroup);
        const mountCenter = new THREE.Vector3(xMm * MM_TO_UNITS, sideY, quarterZ);
        addThreadedMount(group, {
            id: `QuarterJackMount-${index + 1}`,
            axis: 'side',
            facePoint: mountCenter,
            threadRadiusMm: q.diameter * 0.58,
            boreRadiusMm: q.diameter * 0.5,
            baseThreadLengthMm: 2.8,
            nutRadiusMm: q.ringDiameter * 0.64,
            baseNutThicknessMm: 1.9,
            profile: threadProfile
        });
    });
    const p = config.topSideJacks.power9v;
    const powerZ = topZ - p.zOffsetFromTop * MM_TO_UNITS;
    const powerGroup = new THREE.Group();
    powerGroup.name = 'TopPowerJack';
    const powerDepthMm = Math.max(p.recessDepth + 6.5, config.enclosure.depth * 0.62);
    const powerCenterY = sideY - (powerDepthMm * MM_TO_UNITS) / 2 + faceProtrusionMm * MM_TO_UNITS;
    if (showCutters) {
        const powerHelper = new THREE.Mesh(new THREE.CylinderGeometry((p.diameter / 2) * MM_TO_UNITS * 1.2, (p.diameter / 2) * MM_TO_UNITS * 1.2, powerDepthMm * MM_TO_UNITS, 24), new THREE.MeshBasicMaterial({
            color: '#00d5ff',
            transparent: true,
            opacity: 0.55,
            depthTest: false,
            side: THREE.DoubleSide
        }));
        powerHelper.position.set(0, powerCenterY, powerZ);
        powerHelper.renderOrder = 1000;
        group.add(powerHelper);
    }
    const powerLip = new THREE.Mesh(new THREE.TorusGeometry((p.diameter * 0.3) * MM_TO_UNITS, Math.max((p.diameter * 0.06) * MM_TO_UNITS, 0.00065), 12, 28), new THREE.MeshStandardMaterial({ color: '#9ba1a8', metalness: 0.7, roughness: 0.38 }));
    powerLip.rotation.x = Math.PI / 2;
    powerLip.position.y = sideY + 0.0007;
    powerGroup.add(powerLip);
    powerGroup.position.set(0, 0, powerZ);
    group.add(powerGroup);
};
export const buildPedalGroup = (config, options = {}) => {
    const group = new THREE.Group();
    group.name = 'Pedal';
    const topShape = roundedRectShape(config.enclosure.width, config.enclosure.depth, config.enclosure.cornerRadius);
    const bodyGeometry = new THREE.ExtrudeGeometry(topShape, {
        depth: config.enclosure.height,
        bevelEnabled: true,
        bevelSize: config.enclosure.bevelSize,
        bevelThickness: config.enclosure.bevelSize,
        bevelSegments: config.enclosure.bevelSegments,
        curveSegments: 24
    });
    bodyGeometry.scale(MM_TO_UNITS, MM_TO_UNITS, MM_TO_UNITS);
    const bodyMaps = options.bodyMaps ?? makeRawAluminumMaps(config.bodyMaterial.color);
    const bodyMaterial = new THREE.MeshStandardMaterial({
        color: '#ffffff',
        map: bodyMaps.colorMap,
        bumpMap: bodyMaps.roughnessMap,
        bumpScale: 0.034,
        roughnessMap: bodyMaps.roughnessMap,
        normalMap: bodyMaps.normalMap ?? null,
        normalScale: bodyMaps.normalMap ? new THREE.Vector2(1.05, 1.05) : new THREE.Vector2(1, 1),
        metalness: 0.72,
        roughness: 0.46
    });
    const topZ = (config.enclosure.height + config.enclosure.bevelSize) * MM_TO_UNITS;
    const baseBodyMesh = new THREE.Mesh(bodyGeometry, bodyMaterial);
    const bodyMesh = carveTopSideJackOpenings(baseBodyMesh, config, topZ);
    reprojectBodyUVs(bodyMesh, config);
    group.add(bodyMesh);
    const threadProfile = getThreadProfile(config);
    config.knobLayout.rows.forEach((row) => {
        const xPositions = equallySpacedX(row.count, row.spacing);
        xPositions.forEach((xMm, index) => {
            const knobId = `${row.id}-${index + 1}`;
            addFrontHoleRecess(group, knobId, xMm, row.y, config.knobLayout.holeDiameter, topZ);
            const knobGeometry = createKnobGeometry(config.knobLayout.radius, config.knobLayout.height, config.knobLayout.topRoundness);
            const material = new THREE.MeshStandardMaterial({
                color: config.knobLayout.color,
                metalness: 0.15,
                roughness: config.knobLayout.roughness
            });
            const mesh = new THREE.Mesh(knobGeometry, material);
            mesh.name = `Knob-${knobId}`;
            mesh.rotation.x = Math.PI / 2;
            const holeZ = topZ + 0.001;
            const knobBaseZ = topZ;
            const knobHeightUnits = config.knobLayout.height * MM_TO_UNITS;
            const knobTopZ = knobBaseZ + knobHeightUnits;
            const knobCenter = new THREE.Vector3(xMm * MM_TO_UNITS, row.y * MM_TO_UNITS, knobBaseZ);
            mesh.position.set(knobCenter.x, knobCenter.y, knobCenter.z);
            group.add(mesh);
            const stripeMaterial = new THREE.MeshStandardMaterial({
                color: config.knobLayout.stripeColor,
                metalness: 0.05,
                roughness: 0.35
            });
            const stripeWidth = Math.max(config.knobLayout.stripeWidth * MM_TO_UNITS, 0.0004);
            const stripeDepth = Math.max(config.knobLayout.stripeDepth * MM_TO_UNITS, 0.00018);
            const stripeTopLength = config.knobLayout.radius * MM_TO_UNITS * 0.9;
            const topStripe = new THREE.Mesh(new THREE.BoxGeometry(stripeTopLength, stripeWidth, stripeDepth), stripeMaterial);
            topStripe.name = `KnobStripeTop-${knobId}`;
            topStripe.position.set(knobCenter.x + stripeTopLength / 2, knobCenter.y, knobTopZ + stripeDepth / 2 + 0.00005);
            group.add(topStripe);
            const baseClearance = Math.max(config.knobLayout.topRoundness * MM_TO_UNITS * 0.35, 0.0012);
            const maxStripeDrop = Math.max(knobHeightUnits - baseClearance, knobHeightUnits * 0.55);
            const sideStripeDrop = Math.min(config.knobLayout.stripeDrop * MM_TO_UNITS, maxStripeDrop);
            const sideStripe = new THREE.Mesh(new THREE.BoxGeometry(stripeDepth, stripeWidth, sideStripeDrop), stripeMaterial);
            sideStripe.name = `KnobStripeSide-${knobId}`;
            sideStripe.position.set(knobCenter.x + config.knobLayout.radius * MM_TO_UNITS - stripeDepth / 2, knobCenter.y, knobTopZ - sideStripeDrop / 2);
            group.add(sideStripe);
            addThreadedMount(group, {
                id: `KnobMount-${knobId}`,
                axis: 'front',
                facePoint: new THREE.Vector3(xMm * MM_TO_UNITS, row.y * MM_TO_UNITS, holeZ),
                threadRadiusMm: config.knobLayout.holeDiameter * 0.45,
                baseThreadLengthMm: 2.2,
                nutRadiusMm: config.knobLayout.holeDiameter * 0.72,
                baseNutThicknessMm: 1.4,
                profile: threadProfile
            });
        });
    });
    config.holes.forEach((hole) => addFrontHoleRecess(group, hole.id, hole.x, hole.y, hole.diameter, topZ));
    config.toggleSwitches.forEach((sw) => {
        addFrontHoleRecess(group, sw.id, sw.x, sw.y, sw.holeDiameter, topZ);
        const bushing = new THREE.Mesh(new THREE.CylinderGeometry(sw.bushingRadius * MM_TO_UNITS, sw.bushingRadius * MM_TO_UNITS, sw.bushingHeight * MM_TO_UNITS, 26), new THREE.MeshStandardMaterial({ color: '#d8dbe0', metalness: 0.96, roughness: 0.22 }));
        bushing.rotation.x = Math.PI / 2;
        bushing.position.set(sw.x * MM_TO_UNITS, sw.y * MM_TO_UNITS, topZ + (sw.bushingHeight * MM_TO_UNITS) / 2);
        bushing.name = `SwitchBushing-${sw.id}`;
        group.add(bushing);
        addThreadedMount(group, {
            id: `SwitchMount-${sw.id}`,
            axis: 'front',
            facePoint: new THREE.Vector3(sw.x * MM_TO_UNITS, sw.y * MM_TO_UNITS, topZ),
            threadRadiusMm: sw.holeDiameter * 0.43,
            baseThreadLengthMm: sw.bushingHeight,
            nutRadiusMm: sw.holeDiameter * 0.72,
            baseNutThicknessMm: 1.4,
            profile: threadProfile
        });
        const leverPivot = new THREE.Group();
        leverPivot.name = `SwitchLeverPivot-${sw.id}`;
        leverPivot.position.set(sw.x * MM_TO_UNITS, sw.y * MM_TO_UNITS, topZ + sw.bushingHeight * MM_TO_UNITS);
        leverPivot.rotation.x = stateToTilt(sw.position);
        const lever = new THREE.Mesh(new THREE.CylinderGeometry(sw.leverRadius * MM_TO_UNITS, sw.leverRadius * MM_TO_UNITS, sw.leverHeight * MM_TO_UNITS, 18), new THREE.MeshStandardMaterial({ color: '#d9dde2', metalness: 0.95, roughness: 0.2 }));
        lever.rotation.x = Math.PI / 2;
        lever.position.z = (sw.leverHeight * MM_TO_UNITS) / 2;
        lever.name = `SwitchLever-${sw.id}`;
        leverPivot.add(lever);
        const leverCap = new THREE.Mesh(new THREE.SphereGeometry(sw.leverRadius * MM_TO_UNITS, 16, 14), new THREE.MeshStandardMaterial({ color: '#e2e6eb', metalness: 0.96, roughness: 0.18 }));
        leverCap.position.z = sw.leverHeight * MM_TO_UNITS;
        leverCap.name = `SwitchLeverCap-${sw.id}`;
        leverPivot.add(leverCap);
        group.add(leverPivot);
    });
    config.leds.forEach((led) => {
        const hole = findHole(config, led.holeId);
        const bodyHeight = Math.max(led.height - led.radius * 0.9, led.height * 0.58);
        const ledBody = new THREE.Mesh(new THREE.CylinderGeometry(led.radius * MM_TO_UNITS, led.radius * MM_TO_UNITS, bodyHeight * MM_TO_UNITS, 24), new THREE.MeshStandardMaterial({
            color: '#f8fff8',
            transparent: true,
            opacity: 0.9,
            emissive: new THREE.Color(led.color),
            emissiveIntensity: led.intensity * 0.65,
            roughness: 0.22,
            metalness: 0.02
        }));
        ledBody.rotation.x = Math.PI / 2;
        ledBody.position.set(hole.x * MM_TO_UNITS, hole.y * MM_TO_UNITS, topZ + (bodyHeight * MM_TO_UNITS) / 2);
        ledBody.name = `LEDBody-${led.id}`;
        group.add(ledBody);
        const material = new THREE.MeshStandardMaterial({
            color: '#f8fff8',
            transparent: true,
            opacity: 0.92,
            emissive: new THREE.Color(led.color),
            emissiveIntensity: led.intensity,
            roughness: 0.18,
            metalness: 0
        });
        const dome = new THREE.Mesh(new THREE.SphereGeometry(led.radius * MM_TO_UNITS, 18, 16), material);
        dome.name = `LED-${led.id}`;
        dome.position.set(hole.x * MM_TO_UNITS, hole.y * MM_TO_UNITS, topZ + bodyHeight * MM_TO_UNITS);
        group.add(dome);
    });
    config.footswitches.forEach((footswitch) => {
        const hole = findHole(config, footswitch.holeId);
        const material = new THREE.MeshStandardMaterial({
            color: footswitch.color,
            metalness: 0.95,
            roughness: footswitch.roughness
        });
        const shaftHeight = Math.max(footswitch.height - footswitch.topSectionHeight, 1.2);
        const shaft = new THREE.Mesh(new THREE.CylinderGeometry(footswitch.shaftRadius * MM_TO_UNITS, footswitch.shaftRadius * MM_TO_UNITS, shaftHeight * MM_TO_UNITS, 30), material);
        shaft.name = `FootswitchShaft-${footswitch.id}`;
        shaft.rotation.x = Math.PI / 2;
        shaft.position.set(hole.x * MM_TO_UNITS, hole.y * MM_TO_UNITS, topZ + (shaftHeight * MM_TO_UNITS) / 2);
        group.add(shaft);
        const topSection = new THREE.Mesh(new THREE.CylinderGeometry(footswitch.radius * MM_TO_UNITS, footswitch.radius * MM_TO_UNITS, footswitch.topSectionHeight * MM_TO_UNITS, 32), material);
        topSection.name = `FootswitchTop-${footswitch.id}`;
        topSection.rotation.x = Math.PI / 2;
        topSection.position.set(hole.x * MM_TO_UNITS, hole.y * MM_TO_UNITS, topZ + shaftHeight * MM_TO_UNITS + (footswitch.topSectionHeight * MM_TO_UNITS) / 2);
        group.add(topSection);
        addThreadedMount(group, {
            id: `FootswitchMount-${footswitch.id}`,
            axis: 'front',
            facePoint: new THREE.Vector3(hole.x * MM_TO_UNITS, hole.y * MM_TO_UNITS, topZ),
            threadRadiusMm: hole.diameter * 0.44,
            baseThreadLengthMm: 2.8,
            nutRadiusMm: hole.diameter * 0.74,
            baseNutThicknessMm: 1.9,
            profile: threadProfile
        });
    });
    const printGeometry = new THREE.PlaneGeometry(config.printableArea.width * MM_TO_UNITS, config.printableArea.depth * MM_TO_UNITS);
    const printMaterial = new THREE.MeshBasicMaterial({
        map: options.screenprintTexture,
        transparent: true,
        opacity: 1,
        alphaTest: 0.18,
        depthWrite: true,
        depthTest: true,
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -1,
        toneMapped: false,
        side: THREE.FrontSide
    });
    const printMesh = new THREE.Mesh(printGeometry, printMaterial);
    printMesh.name = 'Screenprint';
    printMesh.position.set(0, 0, topZ + 0.0008);
    group.add(printMesh);
    addTopSideJacks(group, config, topZ);
    addBackScrews(group, config);
    return group;
};
export const toSerializableConfig = (config) => JSON.stringify(config, null, 2);
export const fromSerializableConfig = (json) => JSON.parse(json);
